import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { BASE_URL } from '../../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
  faFile,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faDownload,
  faRedo,
  faFilter,
  faTableList,
  faPlay,
  faExclamationTriangle,
  faHistory,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
const MySwal = withReactContent(Swal);
const UploadMemberEnrollmentHistory = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyRefreshError, setHistoryRefreshError] = useState(false);
  const historyRequestRef = useRef(null);
  const historyFetchRef = useRef(null);
  const processRequestRef = useRef(false);
  const [refreshingUploadId, setRefreshingUploadId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    original_file_name: '',
    file_type: '',
    status: '',
    created_at: '',
    uploaded_by: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc',
  });
  const statusMap = {
    uploaded: { name: 'Uploaded', color: 'bg-blue-100 text-blue-800', icon: faFile },
    processing: { name: 'Processing...', color: 'bg-yellow-100 text-yellow-800', icon: faSpinner },
    completed: { name: 'Completed', color: 'bg-green-100 text-green-800', icon: faCheckCircle },
    failed: { name: 'Failed', color: 'bg-red-100 text-red-800', icon: faTimesCircle },
  };
  const columns = [
    { key: 'original_file_name', label: 'File Name', sortable: true, icon: faFile },
    { key: 'file_type', label: 'File Type', sortable: true, icon: faFile },
    { key: 'file_size', label: 'File Size (KB)', sortable: true, icon: faDownload },
    { key: 'created_at', label: 'Uploaded At', sortable: true, icon: faClock },
    { key: 'uploaded_by', label: 'Uploaded By', sortable: true, icon: faUser },
    { key: 'status', label: 'Status', sortable: true, icon: faCheckCircle },
    { key: 'success_insert_count', label: 'Success Count', sortable: true, icon: faCheckCircle },
    { key: 'failed_insert_count', label: 'Failed Count', sortable: true, icon: faTimesCircle },
    { key: 'signed_url', label: 'File URL', sortable: false, icon: faDownload },
    { key: 'error_message', label: 'Error Message', sortable: false, icon: faExclamationTriangle },
  ];

  const isValidDateValue = (value) => {
    if (!value) return false;
    if (typeof value === 'string' && value.startsWith('0000-00-00')) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime());
  };

  const formatSafeDate = (value, dateFormat) => {
    if (!isValidDateValue(value)) return '-';
    try {
      return format(new Date(value), dateFormat);
    } catch (error) {
      return '-';
    }
  };
  const fetchUploadHistory = useCallback(async ({ silent = false } = {}) => {
    historyRequestRef.current?.abort();
    const controller = new AbortController();
    historyRequestRef.current = controller;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/members/batch-enrollment/uploads`, {
        signal: controller.signal,
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      if (controller.signal.aborted) return;
      setUploads(response.data.data.data);
      setTotalPages(response.data.data.last_page);
      setHistoryRefreshError(false);
    } catch (error) {
      if (controller.signal.aborted || axios.isCancel(error)) return;
      setHistoryRefreshError(true);
      if (error.response?.status === 401) window.location.href = '/login';
      if (silent) return;
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      } else if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.', { duration: 4000 });
      } else {
        toast.error('Failed to fetch upload history.', { duration: 4000 });
      }
    } finally {
      if (historyRequestRef.current === controller) {
        historyRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    historyFetchRef.current = fetchUploadHistory;
    fetchUploadHistory();
    const refreshProgress = () => {
      if (document.visibilityState !== 'hidden' && !historyRequestRef.current) {
        fetchUploadHistory({ silent: true });
      }
    };
    // Continue polling even if filters/pagination hide an active batch or another user starts it.
    const interval = window.setInterval(refreshProgress, 5000);
    window.addEventListener('focus', refreshProgress);
    document.addEventListener('visibilitychange', refreshProgress);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshProgress);
      document.removeEventListener('visibilitychange', refreshProgress);
      historyRequestRef.current?.abort();
      historyRequestRef.current = null;
      historyFetchRef.current = null;
    };
  }, [fetchUploadHistory]);

  const hasProcessingUploads = uploads.some((upload) => upload.status === 'processing');

  const queueUploads = async (uploadIds) => {
    if (processRequestRef.current) return;
    processRequestRef.current = true;
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/members/batch-enrollment/process`,
        { upload_ids: uploadIds },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      if (!historyFetchRef.current) return;
      const queuedIds = new Set(response.data.queued_uploads || []);
      setUploads((prev) => prev.map((upload) => queuedIds.has(upload.upload_id)
        ? { ...upload, status: 'processing', error_message: null }
        : upload));
      toast.success(response.data.message, { duration: 4000 });
      await historyFetchRef.current({ silent: true });
    } finally {
      processRequestRef.current = false;
      setIsSubmitting(false);
    }
  };
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };
  const retryUpload = async (uploadId) => {
    try {
      await queueUploads([uploadId]);
    } catch (error) {
      MySwal.fire({
        icon: 'error',
        title: 'Retry Failed',
        text: error.response?.data?.message || 'Process failed.',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleDownloadFile = (upload) => {
    if (!upload.signed_url) {
      toast.error('No download link available. Please refresh URL first.', { duration: 4000 });
      return;
    }

    window.open(upload.signed_url, '_blank', 'noopener,noreferrer');
  };

  const handleRefreshUrl = async (upload) => {
    if (!upload.upload_id) {
      toast.error('Upload ID not found.', { duration: 4000 });
      return;
    }

    setRefreshingUploadId(upload.upload_id);
    try {
      const response = await axios.post(
        `${BASE_URL}/members/batch-enrollment/refresh-url`,
        { upload_id: upload.upload_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );
      const result = response.data;

      setUploads((prev) =>
        prev.map((item) =>
          item.upload_id === upload.upload_id
            ? {
                ...item,
                signed_url: result.signed_url,
                url_expires_at: result.expires_at,
              }
            : item
        )
      );

      toast.success(result.message || 'Signed URL refreshed.', { duration: 4000 });
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to refresh this URL.', { duration: 4000 });
        return;
      }

      toast.error(error.response?.data?.message || 'Failed to refresh signed URL.', { duration: 4000 });
    } finally {
      setRefreshingUploadId(null);
    }
  };

  const handleProcessUploads = async () => {
    const selectedUploads = uploads
      .filter((upload) => upload.status === 'uploaded')
      .map((upload) => upload.upload_id);
    if (selectedUploads.length === 0) {
      toast.error('No uploaded files available to process.', { duration: 4000 });
      return;
    }
    MySwal.fire({
      title: 'Process Uploads',
      text: `Are you sure you want to process ${selectedUploads.length} uploaded file(s)?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Process',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await queueUploads(selectedUploads);
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to process uploads.', { duration: 4000 });
        }
      }
    });
  };
  const formatFileSize = (bytes) => {
    return (bytes / 1024).toFixed(0) + ' KB';
  };
  const renderPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const range = [];
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }
    if (currentPage - delta > 2) range.unshift('...');
    if (currentPage + delta < totalPages - 1) range.push('...');
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return pages.map((p, i) => (
      <button
        key={i}
        onClick={() => typeof p === 'number' && handlePageChange(p)}
        disabled={p === '...'}
        className={`p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm ${
          currentPage === p ? 'bg-indigo-500 text-white shadow-md' : ''
        }`}
      >
        {p}
      </button>
    ));
  };
  const LoadingSkeleton = ({ rows, columns }) => (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="hover:bg-gray-50">
          {Array.from({ length: columns }).map((_, j) => (
            <td key={j} className="px-3 py-2">
              <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-6">
      <div className="max-w-1xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-xl overflow-hidden border border-white/20">
          <div className="p-4 sm:p-6 border-b border-gray-200/50">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faTableList} className="mr-2 text-indigo-600" />
                Batch Member Enrollment: View Uploaded File History
              </h1>
              <button
                onClick={() => navigate("/processed-member-batch-enrollment")}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg"
                data-tooltip-id="history-tooltip"
                data-tooltip-content="View history"
              >
                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                View Processed Batch History
              </button>
              
              <button
                onClick={handleProcessUploads}
                disabled={isSubmitting || loading || !uploads.some((upload) => upload.status === 'uploaded')}
                className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                data-tooltip-id="process-tooltip"
                data-tooltip-content="Process selected uploads"
              >
                <FontAwesomeIcon icon={isSubmitting ? faSpinner : faPlay} spin={isSubmitting} className="mr-2" />
                {isSubmitting ? 'Starting...' : 'Process Selected'}
              </button>
            </div>
          </div>
          {/* Filters/Search */}
          <div className="p-4 sm:p-6 bg-gray-50/80">
            <div className="flex flex-col md:flex-row gap-3 items-center">
              <div className="relative flex-1 max-w-md">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="">All Status</option>
                  <option value="uploaded">Uploaded</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faUser}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  type="text"
                  placeholder="Uploaded By"
                  value={filters.uploaded_by}
                  onChange={(e) => handleFilterChange('uploaded_by', e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                aria-label="Items per page"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
              <button
                onClick={() => fetchUploadHistory()}
                className="inline-flex items-center px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-all duration-200 shadow-sm"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
          <div role="status" className="px-4 sm:px-6 py-3 text-sm text-gray-600 flex items-center gap-2">
            {hasProcessingUploads && <FontAwesomeIcon icon={faSpinner} spin className="text-yellow-600" />}
            {hasProcessingUploads
              ? 'Processing is ongoing. Success and failed counts refresh automatically every 5 seconds.'
              : 'History refreshes automatically every 5 seconds.'}
          </div>
          {historyRefreshError && (
            <div role="alert" className="px-4 sm:px-6 pb-3 text-sm text-red-600">
              Unable to refresh progress. Displayed counts may be outdated; use Refresh to try again.
            </div>
          )}
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-all"
                      onClick={column.sortable ? () => handleSort(column.key) : undefined}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={column.icon} className="mr-1 text-xs text-gray-400" />
                        {column.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : uploads.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-4 text-center text-gray-500 text-sm">
                      No uploads found.
                    </td>
                  </tr>
                ) : (
                  uploads.map((upload) => (
                    <tr key={upload.id} className="hover:bg-gray-50 transition-all duration-200">
                      <td className="px-3 py-2 text-sm font-medium">{upload.original_file_name || '-'}</td>
                      <td className="px-3 py-2 text-sm">{upload.file_type || '-'}</td>
                      <td className="px-3 py-2 text-sm">{formatFileSize(upload.file_size)}</td>
                      <td className="px-3 py-2 text-sm">
                        {formatSafeDate(upload.created_at, 'yyyy-MM-dd HH:mm')}
                      </td>
                      <td className="px-3 py-2 text-sm">{upload.uploaded_by || upload.uploaded_by_username || '-'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium flex items-center ${
                            statusMap[upload.status]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={statusMap[upload.status]?.icon || faSpinner}
                            className="mr-1 text-xs"
                            spin={upload.status === 'processing'}
                          />
                          {statusMap[upload.status]?.name || 'Unknown'}
                        </span>
                        {upload.status === 'processing' && (
                          <div className="mt-1 text-xs text-gray-600">
                            {Number(upload.success_insert_count || 0) + Number(upload.failed_insert_count || 0)} rows processed
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-green-600 font-medium">{upload.success_insert_count || '0'}</td>
                      <td className="px-3 py-2 text-sm text-red-600 font-medium">{upload.failed_insert_count || '0'}</td>
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleDownloadFile(upload)}
                            disabled={!upload.signed_url}
                            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed text-sm"
                          >
                            <FontAwesomeIcon icon={faDownload} className="mr-1" />
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRefreshUrl(upload)}
                            disabled={refreshingUploadId === upload.upload_id}
                            className="inline-flex items-center text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed text-xs font-medium"
                            data-tooltip-id="refresh-tooltip"
                            data-tooltip-content="Refresh signed download URL"
                          >
                            <FontAwesomeIcon
                              icon={faSyncAlt}
                              className={`mr-1 ${refreshingUploadId === upload.upload_id ? 'animate-spin' : ''}`}
                            />
                            {refreshingUploadId === upload.upload_id ? 'Refreshing...' : 'Refresh URL'}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-red-600">
                        {upload.error_message ? (
                          <span data-tooltip-id="error-tooltip" data-tooltip-content={upload.error_message}>
                            {upload.error_message.substring(0, 40)}...
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      {upload.status === 'failed' && (
                        <td className="px-3 py-2">
                          <button
                            onClick={() => retryUpload(upload.upload_id)}
                            disabled={isSubmitting}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 text-xs font-medium transition-all"
                          >
                            <FontAwesomeIcon icon={faRedo} className="mr-1" />
                            Retry
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-4 sm:p-6 border-t border-gray-100/50 bg-gray-50/50">
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="mb-3 md:mb-0 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
              aria-label="Items per page"
            >
              {[5, 10, 20, 50, 100].map((val) => (
                <option key={val} value={val}>
                  {val} per page
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <div className="flex items-center gap-1 px-2 py-1">
                {renderPageNumbers()}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-1.5 text-gray-700 rounded-md hover:bg-indigo-100 disabled:opacity-50 transition-all duration-200 shadow-sm"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
        <Tooltip id="refresh-tooltip" place="top" />
        <Tooltip id="process-tooltip" place="top" />
        <Tooltip id="error-tooltip" place="top" />
      </div>
    </div>
  );
};
export default UploadMemberEnrollmentHistory;
